import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { PRIORITIES, PRIORITY_CONFIG } from "@/lib/priority";

/**
 * Edit a lead's own details. Programme membership is deliberately not editable
 * here — a lead can belong to several programmes, each with its own status, so
 * that is managed from the programme side.
 */
export function EditLeadDialog({ lead, trigger, onSaved }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", source: "", priority: "medium" });

  useEffect(() => {
    if (open && lead) {
      setForm({
        name: lead.name || "",
        phone: lead.phone || "",
        source: lead.source || "manual",
        priority: lead.priority || "medium",
      });
    }
  }, [open, lead]);

  async function submit(e) {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/leads/${lead.id}`, form);
      toast.success("Lead updated");
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Edit lead</DialogTitle>
          <DialogDescription>
            Changing the phone number re-points this lead's WhatsApp conversation.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="edit-lead-form">
          <div className="space-y-2">
            <Label>Full name</Label>
            <Input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              data-testid="edit-lead-name"
            />
          </div>
          <div className="space-y-2">
            <Label>WhatsApp / phone (with country code)</Label>
            <Input
              required
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              data-testid="edit-lead-phone"
            />
          </div>
          <div className="space-y-2">
            <Label>Source</Label>
            <Input
              value={form.source}
              onChange={(e) => setForm({ ...form, source: e.target.value })}
              data-testid="edit-lead-source"
            />
          </div>
          <div className="space-y-2">
            <Label>Priority</Label>
            <Select
              value={form.priority}
              onValueChange={(v) => setForm({ ...form, priority: v })}
            >
              <SelectTrigger data-testid="edit-lead-priority">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PRIORITIES.map((p) => {
                  const cfg = PRIORITY_CONFIG[p];
                  const Icon = cfg.icon;
                  return (
                    <SelectItem key={p} value={p}>
                      <span className="flex items-center gap-1.5">
                        <Icon className="h-3.5 w-3.5" /> {cfg.label}
                      </span>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="edit-lead-submit">
              {saving ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
